import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoadCustomContent } from "../../../../Utils/customHooks/UpToDateContent/useLoadCustomContent";
import {
  CUSTOM_CONTENT_PAGE_SIZE,
  useLoadCustomContentInfinite,
} from "../../../../Utils/customHooks/UpToDateContent/useLoadCustomContentInfinite";
import { normalizeCustomContentPostIds } from "../../../../Utils/customHooks/UpToDateContent/normalizeCustomContentPostIds";
import {
  handleFirstImage,
  handleNavigate,
  updateIsEmptyStatus,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import { debounce } from "lodash";
import PostTemplate from "../CommonComponents/PostTemplate";
import { StaticVariables } from "../../../../Utils/StaticVariables";
import { TemplateSectionGridLoader } from "../../../../Screens/DashboardView/MasterTemplateEditor/TemplateSectionGridLoader";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";

const CustomContentDataFront = (props: any) => {
  const itemsPerPage = 3;
  const {
    user_id,
    username,
    title,
    postIds,
    handleRedirect,
    brand_color,
    isLinks,
    component,
    type,
    RenderUI,
    from,
    templateGridLoaderProps,
    designTokens,
    showEmptyInEditor = false,
    onFeedResolved,
  } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<any>(null);
  const isButtonClicked = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const requestedIds = useMemo(
    () => normalizeCustomContentPostIds(postIds),
    [postIds],
  );
  const isEmptySelection = requestedIds.length === 0;
  const isTemplateWebsite = from === "website-template";
  // Website editor + Website LiB both need to page beyond MAX_COUNT (25).
  const useInfinitePicks =
    (isTemplateWebsite || from === "landing-page") && !isEmptySelection;

  const bulkQuery: any = useLoadCustomContent(
    username,
    user_id,
    requestedIds,
    title,
    component?.content_order,
    component?.custom_order,
    undefined,
    useInfinitePicks ? undefined : StaticVariables().MAX_COUNT,
    true,
    component?.shoppable_status_filter || "all",
    !useInfinitePicks,
  );

  const infiniteQuery: any = useLoadCustomContentInfinite(
    username,
    user_id,
    requestedIds,
    title,
    component?.content_order,
    component?.custom_order,
    undefined,
    component?.shoppable_status_filter || "all",
    useInfinitePicks,
  );

  const CustomLinkDataLoading = useInfinitePicks
    ? infiniteQuery.isLoading
    : bulkQuery.isLoading;
  const CustomLinkDataFetching = useInfinitePicks
    ? infiniteQuery.isFetching
    : bulkQuery.isFetching;
  const CustomLinkDataArray = useInfinitePicks
    ? infiniteQuery.data
    : bulkQuery.data;
  const forceRefetch = useInfinitePicks
    ? infiniteQuery.forceRefetch
    : bulkQuery.forceRefetch;
  const fetchNextPage = infiniteQuery.fetchNextPage;
  const hasNextPage = Boolean(infiniteQuery.hasNextPage);
  const isFetchingNextPage = Boolean(infiniteQuery.isFetchingNextPage);

  // Recover if a prior optimistic cache wipe left this query empty while ids remain.
  const emptyCacheRefetchKey = useRef("");
  useEffect(() => {
    if (isEmptySelection) {
      emptyCacheRefetchKey.current = "";
      return;
    }
    if (CustomLinkDataLoading || CustomLinkDataFetching) {
      return;
    }
    const raw = Array.isArray(CustomLinkDataArray?.data)
      ? CustomLinkDataArray.data
      : Array.isArray(CustomLinkDataArray)
        ? CustomLinkDataArray
        : [];
    if (raw.length > 0) return;
    const key = requestedIds.join(",");
    if (!key || emptyCacheRefetchKey.current === key) return;
    emptyCacheRefetchKey.current = key;
    forceRefetch?.();
  }, [
    isEmptySelection,
    CustomLinkDataLoading,
    CustomLinkDataFetching,
    CustomLinkDataArray,
    requestedIds,
    forceRefetch,
  ]);

  const allData: any[] = useMemo(() => {
    if (isEmptySelection) return [];
    const data = Array.isArray(CustomLinkDataArray?.data)
      ? CustomLinkDataArray.data
      : [];
    const orderType = String(component?.content_order || "DESC")
      .trim()
      .toUpperCase();
    const isCustomOrder =
      orderType === "CUSTOM" || orderType.startsWith("CUSTOM");

    if (useInfinitePicks) {
      // Infinite loader already applies CUSTOM vs ASC/DESC per page.
      if (!isCustomOrder) {
        const asc = orderType === "ASC";
        return [...data].sort((a: any, b: any) => {
          const aMs = Date.parse(String(a?.post_date || a?.posted_at || "")) || 0;
          const bMs = Date.parse(String(b?.post_date || b?.posted_at || "")) || 0;
          if (aMs !== bMs) return asc ? aMs - bMs : bMs - aMs;
          return 0;
        });
      }
      return data;
    }

    // Landing / Website LiB: only force pick-id order for CUSTOM.
    // ASC/DESC must keep date sort from the RPC (or sort client-side).
    if (isCustomOrder) {
      const customIds = normalizeCustomContentPostIds(component?.custom_order);
      const orderIds = customIds.length ? customIds : requestedIds;
      const byId = new Map(
        data.map((post: any) => [String(post?.post_id ?? ""), post]),
      );
      return orderIds
        .map((id) => byId.get(String(id)))
        .filter(Boolean) as any[];
    }

    const selected = new Set(requestedIds.map((id) => String(id)));
    const rows = data.filter((post: any) =>
      selected.has(String(post?.post_id ?? "")),
    );
    const asc = orderType === "ASC";
    return [...rows].sort((a: any, b: any) => {
      const aMs = Date.parse(String(a?.post_date || a?.posted_at || "")) || 0;
      const bMs = Date.parse(String(b?.post_date || b?.posted_at || "")) || 0;
      if (aMs !== bMs) return asc ? aMs - bMs : bMs - aMs;
      return 0;
    });
  }, [
    isEmptySelection,
    requestedIds,
    CustomLinkDataArray?.data,
    useInfinitePicks,
    component?.content_order,
    component?.custom_order,
  ]);

  const showLoader =
    !isEmptySelection &&
    (CustomLinkDataLoading || isLoading) &&
    allData.length === 0;

  useEffect(() => {
    if (isEmptySelection) {
      setIsLoading(false);
      return;
    }
    if (CustomLinkDataLoading) setIsLoading(true);
  }, [CustomLinkDataLoading, isEmptySelection]);

  useEffect(() => {
    const checkEmptyStatus = async () => {
      if (isEmptySelection) {
        setIsLoading(false);
        return;
      }
      if (!CustomLinkDataLoading) {
        if (allData.length === 0) {
          if (!showEmptyInEditor) {
            await updateIsEmptyStatus(title, user_id, component.id, from);
          }
          setIsLoading(false);
        } else {
          setIsLoading(false);
          void handleFirstImage(
            title,
            component?.id,
            allData?.[0],
            user_id,
            from,
          );
        }
      }
    };
    void checkEmptyStatus();
  }, [CustomLinkDataLoading, allData, isEmptySelection]);

  const handlePrevPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleNextPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) =>
      Math.min(prevIndex + 1, allData.length - itemsPerPage),
    );
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleScroll = useCallback(
    debounce(() => {
      if (!isButtonClicked.current) {
        if (containerRef.current) {
          const scrollLeft = containerRef.current.scrollLeft;
          const itemWidth =
            containerRef.current.scrollWidth / Math.max(allData.length, 1);
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newCurrentIndex);
        }
      }
    }, 100),
    [allData],
  );

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth =
        containerRef.current.scrollWidth / Math.max(allData.length, 1);
      const scrollPosition = itemWidth * currentIndex;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [currentIndex, allData]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [allData, handleScroll]);

  useEffect(() => {
    if (from !== "website-template" || !onFeedResolved) return;
    if (showLoader) return;
    onFeedResolved(allData.length > 0);
  }, [from, onFeedResolved, showLoader, allData.length]);

  return (
    <>
      {showLoader ? (<StayFeedLoadingPlaceholder
          designTokens={designTokens}
          from={from}
          component={component}
          templateGridLoaderProps={templateGridLoaderProps}
        />) : allData.length === 0 ? (
        showEmptyInEditor ? (
          <FeedSectionEmptyState
            title={title}
            designTokens={designTokens}
            message={websiteEditorFeedEmptyMessage("custom-pick-filtered")}
          />
        ) : null
      ) : (
        <>
          {RenderUI ? (
            <>
              <RenderUI
                data={allData}
                currentIndex={currentIndex}
                handleNavigate={handleNavigate}
                component={component}
                handleNextPage={handleNextPage}
                handlePrevPage={handlePrevPage}
                containerRef={containerRef}
                handleRedirect={handleRedirect}
                brand_color={brand_color}
                title={title}
                type={type}
                componentType={component?.type || type}
                isLinks={isLinks}
                from={from}
                username={username}
                user_id={user_id}
                designTokens={designTokens}
                fetchNextPage={
                  useInfinitePicks ? fetchNextPage : undefined
                }
                hasNextPage={useInfinitePicks ? hasNextPage : false}
                isFetchingNextPage={
                  useInfinitePicks ? isFetchingNextPage : false
                }
              />
              {useInfinitePicks && isFetchingNextPage && templateGridLoaderProps ? (
                <div
                  className="mte-tpl-tab-feed-pending"
                  aria-hidden
                  aria-busy="true"
                >
                  <TemplateSectionGridLoader
                    variant={
                      templateGridLoaderProps.variant as "shop" | "content"
                    }
                    device={
                      templateGridLoaderProps.device as
                        | "desktop"
                        | "tablet"
                        | "mobile"
                    }
                    tileLayout="feed"
                    count={CUSTOM_CONTENT_PAGE_SIZE}
                  />
                </div>
              ) : null}
            </>
          ) : allData.length > 0 ? (
            <PostTemplate
              processedData={allData}
              total_count={
                useInfinitePicks ? requestedIds.length : allData.length
              }
              component={component}
              user_id={user_id}
              username={username}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              from={from}
              type={type}
              itemsPerPage={itemsPerPage}
              platform={type}
              pinnedPostIds={component?.pinned_posts?.split(",") || []}
              isLinks={isLinks}
              designTokens={designTokens}
            />
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
};

export default CustomContentDataFront;
